import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Button,
  Snackbar,
  Alert,
  Box,
  Divider,
  Chip,
  Stack,
  Paper,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VerifiedIcon from "@mui/icons-material/Verified";
import BlockIcon from "@mui/icons-material/Block";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import BoltIcon from "@mui/icons-material/Bolt";
import api from "../../utils/api";

const STATUS_OPTIONS = [
  { label: "Draft", value: "Draft" },
  { label: "Upcoming", value: "Upcoming" },
  { label: "Active", value: "Active" },
  { label: "Closed", value: "Closed" },
  { label: "Archived", value: "Archived" },
];

// FL Statute Chapter 718 governs condominiums (§718.128 electronic voting);
// Chapter 720 governs homeowners' associations (§720.317 electronic voting).
// A single election picks which one applies, or neither.
const COMPLIANCE_CHAPTER_OPTIONS = [
  { value: "", label: "None (no statute-specific compliance mode)" },
  { value: "condo", label: "Condominium: FS §718.128 (Chapter 718)" },
  { value: "hoa", label: "Homeowners’ Association: FS §720.317 (Chapter 720)" },
];
const COMPLIANCE_STATUTE = { condo: "FS §718.128", hoa: "FS §720.317" };

// Distinct ClubStatusRuleCode values found in the member database (hyphens
// and casing normalized — the raw column has both "A-PR" and "APR" for the
// same status). Their exact meaning per code isn't documented anywhere in
// the system, so they're presented as-is rather than with invented labels —
// an admin restricting eligibility needs to already know what these mean.
const MEMBER_TYPE_OPTIONS = ["AMB", "APR", "AGU", "ATR", "ART", "ABE", "Z", "EVENT"];

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

// `dt` here is the raw value of a <input type="datetime-local"> field, which
// has no timezone marker and is meant to represent the admin's own local
// wall-clock time (e.g. "2026-09-02T11:44"). `new Date(...)` on a string in
// exactly that form is specified to parse as local time, so this correctly
// yields the true UTC instant. Keep the "Z" suffix — an unambiguous ISO
// string is what lets the backend parse it correctly regardless of the
// server's own timezone; stripping it (as this used to do) made the backend
// re-interpret an already-UTC value as its own local time, shifting it again.
const sqlDateFormat = (dt) => {
  if (!dt) return null;
  return new Date(dt).toISOString();
};

// Inverse of the above: format a UTC Date/ISO-string as the local wall-clock
// value a <input type="datetime-local"> expects. Must use the local getters
// (getFullYear/getHours/...), not toISOString(), which would hand back UTC
// digits mislabeled as local and shift the displayed time by the browser's
// UTC offset.
const toLocalDatetimeInputValue = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const SettingsTab = ({ election, onDelete, onElectionUpdated }) => {
  const [form, setForm] = useState({
    title: "",
    description: "",
    start_datetime: "",
    end_datetime: "",
    status: "Draft",
    is_anonymous: true,
    show_results: false,
    require_pin: false,
    max_votes_per_member: 1,
    property_based_voting: false,
    allowed_member_types: [],
    compliance_chapter: "",
    require_consent: false,
    board_approved: false,
    board_resolution_number: "",
  });

  const [saving, setSaving]           = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const hasEditedRef = useRef(false);

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const handleChange = (field, value) => {
    hasEditedRef.current = true;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Both FS §718.128 (condo) and §720.317 (HOA) require member consent to
  // electronic voting, not just ballot-secrecy — so picking either chapter
  // also turns consent on by default. Admins can still switch consent back
  // off independently. Picking "None" leaves consent as whatever it was.
  const handleComplianceChapterChange = (chapter) => {
    hasEditedRef.current = true;
    setForm((prev) => ({
      ...prev,
      compliance_chapter: chapter,
      require_consent: chapter ? true : prev.require_consent,
    }));
  };

  useEffect(() => {
    if (!election) return;
    if (hasEditedRef.current) return;

    setForm({
      title: election.title || "",
      description: election.description || "",
      start_datetime: toLocalDatetimeInputValue(election.start_datetime),
      end_datetime: toLocalDatetimeInputValue(election.end_datetime),
      status: election.status || "Draft",
      is_anonymous: Number(election.is_anonymous) === 1,
      show_results: Number(election.show_results) === 1,
      require_pin: Number(election.require_pin) === 1,
      max_votes_per_member: Number(election.max_votes_per_member ?? 1),
      property_based_voting: Number(election.property_based_voting) === 1,
      allowed_member_types: String(election.allowed_member_types || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      // Rows saved before this chapter picker existed only have
      // fs_718128_compliance=1 with no chapter — fall back to "condo",
      // the only chapter this system cited before this feature.
      compliance_chapter: election.compliance_chapter
        || (Number(election.fs_718128_compliance) === 1 ? "condo" : ""),
      require_consent: election.require_consent === true || Number(election.require_consent) === 1,
      board_approved: election.board_approved === true || Number(election.board_approved) === 1,
      board_resolution_number: election.board_resolution_number || "",
    });
  }, [election]);

  const startDate = useMemo(
    () => (form.start_datetime ? new Date(form.start_datetime) : null),
    [form.start_datetime]
  );

  const endDate = useMemo(
    () => (form.end_datetime ? new Date(form.end_datetime) : null),
    [form.end_datetime]
  );

  const now = useMemo(
    () => new Date(),
    [form.start_datetime, form.end_datetime, form.status]
  );

  const dateRangeValid = useMemo(() => {
    if (!startDate || !endDate) return false;
    return startDate <= endDate;
  }, [startDate, endDate]);

  const accessState = useMemo(() => {
    const status = String(form.status || "").toLowerCase();
    const hasDates = !!startDate && !!endDate;
    const inWindow = hasDates ? now >= startDate && now <= endDate : false;

    const votingAllowed = status === "active" && hasDates && inWindow;
    const sharingAllowed = (status === "active" || status === "upcoming") && hasDates;
    const resultsVisible =
      !!form.show_results || status === "closed" || status === "archived";

    return { status, hasDates, inWindow, votingAllowed, sharingAllowed, resultsVisible };
  }, [form.status, form.show_results, startDate, endDate, now]);

  const handleSave = async () => {
    if (!election?.id) return;

    if (form.start_datetime && form.end_datetime && !dateRangeValid) {
      showSnackbar("End Date must be after Start Date.", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        start_datetime: sqlDateFormat(form.start_datetime),
        end_datetime: sqlDateFormat(form.end_datetime),
        status: form.status,
        is_anonymous: form.is_anonymous ? 1 : 0,
        show_results: form.show_results ? 1 : 0,
        require_pin: form.require_pin ? 1 : 0,
        max_votes_per_member: Number(form.max_votes_per_member ?? 1),
        property_based_voting: form.property_based_voting ? 1 : 0,
        allowed_member_types: (form.allowed_member_types || []).join(","),
        compliance_chapter: form.compliance_chapter || null,
        require_consent: form.require_consent ? 1 : 0,
        board_approved: form.board_approved ? 1 : 0,
        board_resolution_number: form.board_resolution_number || null,
      };

      // Only stamp an approver when approval is newly granted — re-saving
      // other settings while already approved shouldn't reassign credit,
      // and revoking it should clear the record.
      const wasApproved =
        election.board_approved === true || Number(election.board_approved) === 1;
      if (form.board_approved && !wasApproved) {
        payload.board_approved_by = Number(localStorage.getItem("userId")) || null;
      } else if (!form.board_approved) {
        payload.board_approved_by = null;
      }

      await api.put(`/elections/${election.id}`, payload);

      const fresh = await api.get(`/elections/${election.id}`);
      onElectionUpdated?.(fresh.data);

      hasEditedRef.current = false;
      showSnackbar("Election settings saved!", "success");
    } catch (error) {
      console.error("Failed to save settings:", error);
      showSnackbar(
        error.response?.data?.error || "Failed to save election settings",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!election?.id) return;
    setDeleteDialogOpen(false);
    try {
      await api.delete(`/elections/${election.id}`);
      showSnackbar("Election deleted successfully!", "success");
      setTimeout(() => onDelete && onDelete(), 300);
    } catch (error) {
      console.error("Failed to delete election:", error);
      showSnackbar("Failed to delete election", "error");
    }
  };

  const setActiveNow = () => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 7);
    handleChange("status", "Active");
    handleChange("start_datetime", toLocalDatetimeInputValue(start));
    handleChange("end_datetime", toLocalDatetimeInputValue(end));
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(320px, 1fr)" },
          alignItems: "start",
        }}
      >
        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
              >
                Election Settings
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Configure schedule, voting rules, privacy, and results visibility.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<BoltIcon />}
                onClick={setActiveNow}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Set Active (1 week)
              </Button>

              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  backgroundColor: "#2f63ad",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#275592",
                    boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                  },
                }}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          <Typography
            variant="subtitle2"
            sx={{
              mb: 1.5,
              fontWeight: 800,
              color: "#223142",
              letterSpacing: 0.2,
            }}
          >
            Basic
          </Typography>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Election Title"
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Description"
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  mt: 1,
                  mb: 1.5,
                  fontWeight: 800,
                  color: "#223142",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AccessTimeIcon fontSize="small" />
                Schedule & Status
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 0.5,
                  borderRadius: "12px",
                  border: `1px solid ${form.board_approved ? "rgba(21,101,192,0.30)" : "rgba(211,47,47,0.25)"}`,
                  backgroundColor: form.board_approved ? "rgba(21,101,192,0.05)" : "rgba(211,47,47,0.04)",
                  transition: "all 0.2s ease",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.board_approved}
                      onChange={(e) => handleChange("board_approved", e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ color: "#223142" }}>
                        Board Resolution Approved{form.compliance_chapter ? ` (${COMPLIANCE_STATUTE[form.compliance_chapter]})` : ""}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6b778c", display: "block", mt: 0.25 }}>
                        Required before this election can be set to Active or Upcoming. Voting and
                        invites stay blocked until this is checked.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", gap: 1 }}
                />
                {form.board_approved && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Board Resolution Number (optional)"
                    value={form.board_resolution_number}
                    onChange={(e) => handleChange("board_resolution_number", e.target.value)}
                    sx={{
                      mt: 1.5,
                      "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" },
                    }}
                  />
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={form.status}
                onChange={(e) => handleChange("status", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={
                  form.property_based_voting
                    ? "Max votes per property"
                    : "Max votes per member"
                }
                helperText={
                  form.property_based_voting
                    ? "With property-based voting on, all members sharing a property/household share this many votes total."
                    : " "
                }
                value={form.max_votes_per_member}
                onChange={(e) =>
                  handleChange("max_votes_per_member", Number(e.target.value))
                }
                inputProps={{ min: 1, step: 1 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={form.start_datetime}
                onChange={(e) => handleChange("start_datetime", e.target.value)}
                error={!!form.end_datetime && !!form.start_datetime && !dateRangeValid}
                helperText={
                  !!form.end_datetime && !!form.start_datetime && !dateRangeValid
                    ? "Start must be before End."
                    : " "
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={form.end_datetime}
                onChange={(e) => handleChange("end_datetime", e.target.value)}
                error={!!form.end_datetime && !!form.start_datetime && !dateRangeValid}
                helperText={
                  !!form.end_datetime && !!form.start_datetime && !dateRangeValid
                    ? "End must be after Start."
                    : " "
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  mt: 1,
                  mb: 1.5,
                  fontWeight: 800,
                  color: "#223142",
                }}
              >
                Voting Options
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_anonymous}
                      onChange={(e) => handleChange("is_anonymous", e.target.checked)}
                    />
                  }
                  label="Anonymous Voting"
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.show_results}
                      onChange={(e) => handleChange("show_results", e.target.checked)}
                    />
                  }
                  label="Show Results Automatically"
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.require_pin}
                      onChange={(e) => handleChange("require_pin", e.target.checked)}
                    />
                  }
                  label="Require PIN for Voting"
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.property_based_voting}
                      onChange={(e) =>
                        handleChange("property_based_voting", e.target.checked)
                      }
                    />
                  }
                  label="Property-based voting"
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={MEMBER_TYPE_OPTIONS}
                value={form.allowed_member_types}
                onChange={(e, newValue) =>
                  handleChange("allowed_member_types", newValue)
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Eligible member types"
                    helperText="Leave empty to allow all member types. Only members with a matching status code will be invited. Codes come from the member database and aren't relabeled here."
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Florida statute compliance chapter — condo (718) vs HOA (720), or none */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: `1px solid ${form.compliance_chapter ? "rgba(21,101,192,0.30)" : "rgba(47,99,173,0.08)"}`,
                  backgroundColor: form.compliance_chapter ? "rgba(21,101,192,0.05)" : "rgba(47,99,173,0.02)",
                  transition: "all 0.2s ease",
                }}
              >
                <TextField
                  select
                  fullWidth
                  label="Florida Statute Compliance Framework"
                  value={form.compliance_chapter}
                  onChange={(e) => handleComplianceChapterChange(e.target.value)}
                  helperText={
                    form.compliance_chapter
                      ? `ON (${COMPLIANCE_STATUTE[form.compliance_chapter]}). Voter email and IP address will NOT be collected or displayed. Ballot identity is fully separated. Recommended for board elections.`
                      : "OFF. Voter email and IP address are recorded for audit purposes. Suitable for non-board votes and surveys."
                  }
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" } }}
                >
                  {COMPLIANCE_CHAPTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || "none"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Divider sx={{ my: 1.5 }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.require_consent}
                      onChange={(e) => handleChange("require_consent", e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ color: "#223142" }}>
                        Require Member Consent Before Inviting
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6b778c", display: "block", mt: 0.25 }}>
                        {form.require_consent
                          ? `ON${form.compliance_chapter ? ` (${COMPLIANCE_STATUTE[form.compliance_chapter]})` : ""}. A member with no recorded electronic-voting consent will be skipped when invites are sent. Manage consent records in Consent & Settings.`
                          : "OFF. Invites are sent regardless of recorded consent."}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", gap: 1 }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1.5 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, color: "#223142" }}>
                    Danger zone
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    This permanently deletes the election and related data.
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Delete Election
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Card>

        <Paper
          elevation={0}
          sx={{
            ...adminCardSx,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2.5,
              backgroundColor: "rgba(47,99,173,0.02)",
              borderBottom: "1px solid rgba(47,99,173,0.08)",
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                color: "#223142",
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <VerifiedIcon fontSize="small" />
              Access Rules
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Voting, sharing, and results visibility should follow status and schedule.
            </Typography>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ color: "#6b778c" }}>Status</Typography>
                <Chip label={form.status} />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ color: "#6b778c" }}>Has start / end</Typography>
                <Chip
                  label={accessState.hasDates ? "Yes" : "No"}
                  color={accessState.hasDates ? "success" : "default"}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ color: "#6b778c" }}>In voting window</Typography>
                <Chip
                  label={accessState.inWindow ? "Yes" : "No"}
                  color={accessState.inWindow ? "success" : "default"}
                />
              </Box>

              <Divider />

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                  Share link / invites
                </Typography>
                <Chip
                  icon={accessState.sharingAllowed ? <VerifiedIcon /> : <BlockIcon />}
                  label={accessState.sharingAllowed ? "Allowed" : "Blocked"}
                  color={accessState.sharingAllowed ? "success" : "default"}
                  variant={accessState.sharingAllowed ? "filled" : "outlined"}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                  Voting
                </Typography>
                <Chip
                  icon={accessState.votingAllowed ? <VerifiedIcon /> : <BlockIcon />}
                  label={accessState.votingAllowed ? "Allowed" : "Blocked"}
                  color={accessState.votingAllowed ? "success" : "default"}
                  variant={accessState.votingAllowed ? "filled" : "outlined"}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                  Results
                </Typography>
                <Chip
                  label={accessState.resultsVisible ? "Visible" : "Hidden"}
                  color={accessState.resultsVisible ? "info" : "default"}
                  variant={accessState.resultsVisible ? "filled" : "outlined"}
                />
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: "16px", p: 0.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
          Delete Election?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#5a6578" }}>
            This will permanently delete <strong>{election?.title}</strong> along
            with all its questions, invites, and vote records. This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              color: "#5a6578",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
            }}
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsTab;
